import { uiStore } from 'shared/store/uiStore'
// Imported by module path, not the entities/Conversation barrel, which pulls the
// whole store graph in at import time.
import { ConversationsApi } from 'entities/Conversation/api/conversations'
import { type IResponseConversation } from 'entities/Conversation/api/types'
import { ConversationRoutes } from 'entities/ExportRouters'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type { useEntitiesContext } from 'entities/EntitiesExport'

type Entities = ReturnType<typeof useEntitiesContext>
type InboxesStore = Entities['inboxesStore']

/**
 * Get-or-create the (contact, inbox) conversation. Passes number_id the way the
 * app's own composer does — without it the backend picks a number for
 * multi-number and smart inboxes.
 */
export const createConversationForContact = async (
  inboxesStore: InboxesStore,
  contact_id: number,
  team_id: number
): Promise<IResponseConversation> => {
  const inbox = inboxesStore.getItemShared(team_id)
  const { data } = await ConversationsApi.create({
    contact_id,
    team_id,
    ...(inbox?.numberId ? { number_id: inbox.numberId } : {}),
  })

  return data
}

/**
 * The (contact, inbox) conversation if one already exists, else null. Same
 * lookup the app's own open-conversation click performs.
 */
export const findConversationForContact = async (
  contact_id: number,
  team_id: number
): Promise<IResponseConversation | null> => {
  const {
    data: { results },
  } = await ConversationsApi.getConversationsSearch({
    teams: [team_id],
    contacts: [contact_id],
    page: 1,
  })

  return results[0] ?? null
}

/**
 * Open an unsent draft addressed to the contact — what clicking "message this
 * contact" does when no thread exists yet. Nothing is persisted: the thread is
 * created server-side only if the user actually sends.
 *
 * Routes to the conversations page first so the draft's own route change
 * (`/conversations/0`, a React route) is not issued from a Vue-owned page.
 */
export const openDraftConversationForContact = async (
  entities: Entities,
  contact_id: number,
  team_id: number
): Promise<void> => {
  const [contact, inbox] = await Promise.all([
    entities.contactsStore.getById(contact_id, true),
    entities.inboxesStore.getById(team_id, true, true),
  ])

  if (!contact) throw new Error(`Contact ${contact_id} was not found.`)
  // A draft needs a real inbox to send from; the unified inbox is a view, not one.
  if (!(inbox instanceof Inbox)) throw new Error(`Inbox ${team_id} cannot start a conversation.`)

  await entities.inboxesStore.handleUpdateTeamInbox(team_id)
  uiStore.changeRouteViaVue({ path: `/${ConversationRoutes.root}` })

  await entities.conversationStore.conversationOpenStore.setSignal({
    type: 'open_new_conversation',
    payload: { contact, inbox, numberId: inbox.numberId },
  })
}

/**
 * Switch the user's current inbox and open the thread. The Conversations UI is
 * scoped by inboxesStore.currentInboxId (a persisted user setting), not by the
 * route — changing the route alone leaves the sidebar and list in the previous
 * inbox. changeRouteViaVue crosses the Vue/React boundary so the thread opens.
 */
export const openConversationInInbox = async (
  inboxesStore: InboxesStore,
  conversation_id: number,
  inbox_id?: number
) => {
  if (inbox_id) await inboxesStore.handleUpdateTeamInbox(inbox_id)

  uiStore.changeRouteViaVue({ path: `/${ConversationRoutes.root}/${conversation_id}` })
}

/**
 * Report the inbox the thread actually landed in, plus explicit guidance when
 * it is not the one that was asked for, so the agent can tell the user instead
 * of claiming success.
 */
export const inboxOutcome = (
  conversation: IResponseConversation,
  requested_team_id: number
): { inbox_id: number | undefined; agent_guidance?: string } => {
  const inbox_id = conversation.inbox_id

  if (!inbox_id || inbox_id === requested_team_id) return { inbox_id }

  return {
    inbox_id,
    agent_guidance: `This conversation belongs to inbox ${inbox_id}, not the requested inbox ${requested_team_id}. Tell the user which inbox was used.`,
  }
}
